/* @ngInject */

export default (
  $rootScope,
  helperService,
  Income,
  IncomeType,
  OwnershipType,
  WorkflowType
) => {
  /**
   * Get available income types.
   *
   * @returns {Array}
   */
  const getAvailableIncomeTypes = () => Income.getAvailableIncomeTypes();

  /**
   * Add income to income array.
   *
   * @param {Array} incomes
   * @param {Object} incomeType
   * @param {String} idContact
   * @param {String} idEmployer
   *
   * @returns {Object}
   */
  const incomeAdd = (incomes, incomeType, idContact, idEmployer) => {
    const income = Income.blank(incomeType);
    income.idContact = idContact || '';
    income.idEmployer = idEmployer || '';
    if (incomeType.id === getAvailableIncomeTypes()[1].id) {
      income.business.lastTaxYear.taxYear = parseInt(moment.utc().year(), 10) - 1;
      income.business.previousTaxYear.taxYear = parseInt(moment.utc().year(), 10) - 2;
    }
    incomes.push(income);
    return incomes;
  };

  /**
   * Remove income from income array.
   *
   * @param {Array} incomes
   * @param {Number} index
   *
   * @returns {Array}
   */
  const incomeDelete = (incomes, index) => helperService.removeItemFromArray(incomes, index);

  /**
   * Get total net business income.
   *
   * @param {Number} income
   * @param {String} idContact
   * @param {String} businessYear
   *
   * @returns {Number}
   */
  const getBusinessYear = ({ income, idContact, businessYear }) => {
    const availableIncomeTypes = getAvailableIncomeTypes();
    let year = helperService.isNumber(moment.utc().year(), false);
    const filteredIncome = _.filter(
      income || [],
      (i) => i?.incomeType?.id === availableIncomeTypes[1]?.id && i?.idContact === idContact
    );
    if (filteredIncome?.length && _.isString(businessYear)) {
      const years = _.map(filteredIncome, (i) =>
        helperService.isNumber(i?.business[businessYear]?.taxYear, false)
      );
      year = years?.length ? _.max(years) : year;
    }
    return year;
  };

  /**
   * Get total net business income.
   *
   * @param {Number} totalIncome
   * @param {Number} totalNontaxable
   *
   * @returns {Number}
   */
  const getTotalNetIncome = (totalIncome, totalNontaxable) => {
    let total = 0;
    const currentYear = parseInt(moment.utc().year(), 10);
    const organizationCountry = helperService.getOrganizationCountry().cca2;
    let financialEndDate = new Date(`06/30/${currentYear}`);
    if (organizationCountry === 'NZ') {
      financialEndDate = new Date(`03/31/${currentYear}`);
    }
    const currentNetIncome = helperService.getNetIncome(organizationCountry);
    let netIncome = currentNetIncome[currentYear - 1];
    if (currentNetIncome[currentYear]) {
      netIncome =
        moment.utc(financialEndDate).diff(moment.utc(), 'days', true) > 0
          ? currentNetIncome[currentYear - 1]
          : currentNetIncome[currentYear];
    }
    if (netIncome && netIncome.length) {
      for (let n = 0; n < netIncome.length; n += 1) {
        if (
          (totalIncome >= netIncome[n].incomeFrom && totalIncome <= netIncome[n].incomeTo) ||
          (totalIncome >= netIncome[n].incomeFrom && netIncome[n].incomeTo === '')
        ) {
          total =
            totalIncome -
            ((totalIncome - (netIncome[n].incomeFrom - 1)) * netIncome[n].tax +
              netIncome[n].additionalTax) -
            totalIncome * netIncome[n].medicareLevy +
            totalNontaxable;
        }
      }
    }
    return total;
  };

  /**
   * Get total net business income per year for home loans.
   *
   * @param {Array} income
   * @param {Boolean} isDepreciation
   *
   * @returns {Number}
   */
  const getTotalNetBusinessIncomePerYearForHomeLoans = (income, isDepreciation) => {
    let total = 0;
    let totalLast = 0;
    let totalPrev = 0;
    let totalNetLast = 0;
    let totalNetPrev = 0;
    if (isDepreciation) {
      const totalLastDepreciation = income.reduce(
        (memo, num) =>
          memo + helperService.isNumber(num.business.lastTaxYear.depreciation, true),
        0
      );
      const totalPrevDepreciation = income.reduce(
        (memo, num) =>
          memo + helperService.isNumber(num.business.previousTaxYear.depreciation, true),
        0
      );
      totalLast = income.reduce(
        (memo, num) =>
          memo +
          (helperService.isNumber(num.business.lastTaxYear.profitBeforeTax, true) +
            helperService.isNumber(num.business.lastTaxYear.interestAndOtherAddbacks, true)),
        0
      );
      totalPrev = income.reduce(
        (memo, num) =>
          memo +
          (helperService.isNumber(num.business.previousTaxYear.profitBeforeTax, true) +
            helperService.isNumber(
              num.business.previousTaxYear.interestAndOtherAddbacks,
              true
            )),
        0
      );
      totalNetLast = getTotalNetIncome(totalLast, 0);
      totalNetPrev = getTotalNetIncome(totalPrev, 0);
      if (totalNetLast <= totalNetPrev || totalNetPrev === 0) {
        total = totalLastDepreciation;
      } else if (totalNetLast > totalNetPrev) {
        total = (totalLastDepreciation + totalPrevDepreciation) / 2;
      }
    } else {
      totalLast = income.reduce(
        (memo, num) =>
          memo +
          (helperService.isNumber(num.business.lastTaxYear.profitBeforeTax, true) +
            helperService.isNumber(num.business.lastTaxYear.interestAndOtherAddbacks, true)),
        0
      );
      totalPrev = income.reduce(
        (memo, num) =>
          memo +
          (helperService.isNumber(num.business.previousTaxYear.profitBeforeTax, true) +
            helperService.isNumber(
              num.business.previousTaxYear.interestAndOtherAddbacks,
              true
            )),
        0
      );
      totalNetLast = getTotalNetIncome(totalLast, 0);
      totalNetPrev = getTotalNetIncome(totalPrev, 0);
      if (totalNetLast <= totalNetPrev || totalNetPrev === 0) {
        total = totalLast;
      } else if (totalNetLast > totalNetPrev) {
        total = (totalLast + totalPrev) / 2;
      }
    }
    return total;
  };

  /**
   * Get total PAYG income per contact.
   *
   * @param {Array} income
   * @param {String} idContact
   * @param {String} type
   * @param {String} frequency
   *
   * @returns {Number}
   */
  const getTotalPaygIncomePerContact = (income, idContact, type, frequency) => {
    let total = 0;
    const availableIIncomeTypes = getAvailableIncomeTypes();
    let filteredIncome = [];
    if (income && income.length) {
      filteredIncome = income.filter(
        (i) => i.incomeType.id === availableIIncomeTypes[0].id && i.idContact === idContact
      );
    }
    for (let i = 0; i < filteredIncome.length; i += 1) {
      total +=
        helperService.isNumber(filteredIncome[i].payg[type], true) *
        helperService.getFrequencyValue(filteredIncome[i].payg[frequency], false);
    }
    return total;
  };

  /**
   * Get total business income per contact.
   *
   * @param {Array} income
   * @param {String} idContact
   * @param {Number} year
   * @param {String} yearType
   * @param {String} type
   *
   * @returns {Number}
   */
  const getTotalBusinessIncomePerContact = (income, idContact, year, yearType, type) => {
    let total = 0;
    const availableIIncomeTypes = getAvailableIncomeTypes();
    const currentYear = helperService.isNumber(moment.utc().year(), false);
    const businessYear = helperService.isNumber(year, false);

    let filteredIncome = [];
    if (income && income.length) {
      filteredIncome = income.filter(
        (i) => i.idContact === idContact && i.incomeType.id === availableIIncomeTypes[1].id
      );
    }
    for (let i = 0; i < filteredIncome.length; i += 1) {
      if (currentYear - businessYear < 3) {
        total += helperService.isNumber(filteredIncome[i].business[yearType][type], true);
      }
    }
    return total;
  };

  /**
   * Get monthly rental/boarder income of existing properties by contact.
   *
   * @param {Array} assets
   * @param {String} idContact
   * @param {Object} idOwnershipType
   * @param {String} mfpType
   * @param {Boolean} isUsingForFinanceCalculations
   *
   * @returns {Number}
   */
  const getRentalOrBoarderIncomeOfExistingPropertiesByContact = ({
    assets,
    idContact,
    ownershipType,
    mfpType,
    isUsingForFinanceCalculations
  }) => {
    let properties = assets;
    if (ownershipType?.name === OwnershipType.investment.name) {
      properties = _.filter(
        properties,
        (p) =>
          helperService.isAssetTypeInvestment(p.assetType) ||
          (helperService.isAssetTypeOwnerOccupied(p.assetType) &&
            p?.willItBecomeInvestment &&
            mfpType !== 'currentPosition')
      );
    }
    if (ownershipType?.name === OwnershipType.owner_occupied.name) {
      properties = _.filter(
        properties,
        (p) =>
          helperService.isAssetTypeOwnerOccupied(p.assetType) ||
          (helperService.isAssetTypeInvestment(p.assetType) &&
            p?.willItBecomeOwnerOccupied &&
            mfpType !== 'currentPosition')
      );
    }
    if (isUsingForFinanceCalculations) {
      properties = _.filter(properties, (p) => !p.toBeSold);
    }
    properties = _.filter(
      properties,
      (p) =>
        helperService.isNumber(p.monthlyRentalIncome, true) > 0 &&
        helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
          p.ownership,
          idContact
        ) > 0
    );
    return _.reduce(
      properties,
      (memo, num) =>
        memo +
        helperService.isNumber(num.monthlyRentalIncome, false) *
          (helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
            num.ownership,
            idContact
          ) /
            100),
      0
    );
  };

  /**
   * Get monthly rental/boarder income of of home loan securities.
   *
   * @param {Array} homeLoanSecurities
   * @param {Array} assets
   * @param {String} idContact
   * @param {Object} ownershipType
   * @param {String} workflowType
   * @param {Boolean} isDealOwnerBroker
   * @param {Boolean} haveLendersExisting
   * @param {Boolean} isUsingForFinanceCalculations
   *
   * @returns {Number}
   */
  const getRentalOrBoarderIncomeOfHomeLoanSecuritiesByContact = ({
    homeLoanSecurities,
    assets,
    idContact,
    ownershipType,
    workflowType,
    isDealOwnerBroker,
    haveLendersExisting,
    isUsingForFinanceCalculations
  }) => {
    if (
      $rootScope.hasBrokerToolsEnabled() &&
      isDealOwnerBroker &&
      haveLendersExisting &&
      (workflowType === WorkflowType.available()[4].name ||
        workflowType === WorkflowType.available()[4])
    ) {
      const securities = _.filter(
        homeLoanSecurities,
        (s) =>
          s?.isNew &&
          s?.ownershipType?.name === ownershipType?.name &&
          helperService.isNumber(s.monthlyRentalIncome, true) > 0 &&
          helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
            s.ownership,
            idContact
          ) > 0
      );
      let willItBecome = [];
      if (ownershipType?.name === OwnershipType.owner_occupied.name) {
        willItBecome = _.filter(
          assets,
          (a) =>
            helperService.isAssetTypeOwnerOccupied(a.assetType) &&
            a.willItBecomeInvestment &&
            helperService.isNumber(a.monthlyRentalIncome, true) > 0 &&
            helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
              a.ownership,
              idContact
            ) > 0
        );
      }
      if (ownershipType?.name === OwnershipType.investment.name) {
        willItBecome = _.filter(
          assets,
          (a) =>
            helperService.isAssetTypeInvestment(a.assetType) &&
            a.willItBecomeOwnerOccupied &&
            helperService.isNumber(a.monthlyRentalIncome, true) > 0 &&
            helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
              a.ownership,
              idContact
            ) > 0
        );
      }
      let properties = [];
      if (isUsingForFinanceCalculations) {
        properties = _.filter(_.flatten(securities.concat(willItBecome)), (p) => !p?.toBeSold);
      } else {
        properties = _.flatten(securities.concat(willItBecome));
      }
      return _.reduce(
        properties,
        (memo, num) =>
          memo +
          helperService.isNumber(num.monthlyRentalIncome, false) *
            (helperService.getOwnershipPercentageOfAssetOrLiabilityPerContact(
              num.ownership,
              idContact
            ) /
              100),
        0
      );
    }
    return 0;
  };

  /**
   * Get total net income per contact.
   *
   * @param {Array} income
   * @param {String} mfpType
   * @param {String} idContact
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   * @param {String} workflowType
   * @param {Boolean} isDealOwnerBroker
   * @param {Boolean} haveLendersExisting
   *
   * @returns {Number}
   */
  const getTotalNetIncomeByContact = ({
    income,
    mfpType,
    idContact,
    assets,
    homeLoanSecurities,
    workflow,
    isDealOwnerBroker,
    haveLendersExisting
  }) => {
    const currentYear = parseInt(moment.utc().year(), 10);
    const organizationCountry = helperService.getOrganizationCountry().cca2;
    let totalNetIncome = 0;
    let totalIncome = 0;
    let totalNontaxable = 0;

    let hasIncomePrimaryBuffer = true;
    if (workflow?.isAssetFinance()) {
      hasIncomePrimaryBuffer = $rootScope.CurrentOrganization.hasPrimaryIncomeBufferAF();
    }
    if (workflow?.isHomeLoan()) {
      hasIncomePrimaryBuffer = $rootScope.CurrentOrganization.hasPrimaryIncomeBufferHL();
    }
    const rentalIncomeShadingBuffer = helperService.getPrimaryBuffer({
      workflow,
      bufferType: 'INCOME'
    });
    const totalIncomeShadingBuffer = helperService.getSecondaryBuffer({
      workflow,
      bufferType: 'INCOME'
    });

    let financialEndDate = new Date(`06/30/${currentYear}`);
    if (organizationCountry === 'NZ') {
      financialEndDate = new Date(`03/31/${currentYear}`);
    }
    const currentNetIncome = helperService.getNetIncome(organizationCountry);
    let netIncome = currentNetIncome[currentYear - 1];
    if (currentNetIncome[currentYear]) {
      netIncome =
        moment.utc(financialEndDate).diff(moment.utc(), 'days', true) > 0
          ? currentNetIncome[currentYear - 1]
          : currentNetIncome[currentYear];
    }
    const filteredIncome = _.filter(income, (i) => i.idContact === idContact);
    if (mfpType === 'currentPosition') {
      const currentRentalIncome =
        getRentalOrBoarderIncomeOfExistingPropertiesByContact({
          assets,
          idContact,
          ownershipType: OwnershipType.investment,
          mfpType,
          isUsingForFinanceCalculations: false
        }) * 12;
      const currentBoarderIncome =
        organizationCountry === 'NZ'
          ? getRentalOrBoarderIncomeOfExistingPropertiesByContact({
              assets,
              idContact,
              ownershipType: OwnershipType.owner_occupied,
              mfpType,
              isUsingForFinanceCalculations: false
            }) * 12
          : 0;
      totalIncome += currentRentalIncome + currentBoarderIncome;
    } else if (
      !mfpType ||
      mfpType === 'proposedPosition' ||
      mfpType === 'proposedPositionBuffer'
    ) {
      const currentRentalIncome =
        getRentalOrBoarderIncomeOfExistingPropertiesByContact({
          assets,
          idContact,
          ownershipType: OwnershipType.investment,
          mfpType,
          isUsingForFinanceCalculations: mfpType !== null
        }) * 12;
      const currentBoarderIncome =
        organizationCountry === 'NZ'
          ? getRentalOrBoarderIncomeOfExistingPropertiesByContact({
              assets,
              idContact,
              ownershipType: OwnershipType.owner_occupied,
              mfpType,
              isUsingForFinanceCalculations: mfpType !== null
            }) * 12
          : 0;
      const proposedRentalIncome =
        getRentalOrBoarderIncomeOfHomeLoanSecuritiesByContact({
          homeLoanSecurities,
          assets,
          idContact,
          ownershipType: OwnershipType.investment,
          workflowType: workflow.type,
          isDealOwnerBroker,
          haveLendersExisting,
          isUsingForFinanceCalculations: mfpType !== null
        }) * 12;
      const proposedBoarderIncome =
        organizationCountry === 'NZ'
          ? getRentalOrBoarderIncomeOfHomeLoanSecuritiesByContact({
              homeLoanSecurities,
              assets,
              idContact,
              ownershipType: OwnershipType.owner_occupied,
              workflowType: workflow.type,
              isDealOwnerBroker,
              haveLendersExisting,
              isUsingForFinanceCalculations: mfpType !== null
            }) * 12
          : 0;
      if (mfpType === 'proposedPositionBuffer') {
        totalIncome += hasIncomePrimaryBuffer
          ? (currentRentalIncome +
              proposedRentalIncome +
              currentBoarderIncome +
              proposedBoarderIncome) *
            rentalIncomeShadingBuffer
          : currentRentalIncome +
            proposedRentalIncome +
            currentBoarderIncome +
            proposedBoarderIncome;
      } else {
        totalIncome +=
          currentRentalIncome +
          proposedRentalIncome +
          currentBoarderIncome +
          proposedBoarderIncome;
      }
    }
    let hasTaxPaid = false;
    for (let i = 0; i < filteredIncome.length; i += 1) {
      if (filteredIncome[i].incomeType.id === getAvailableIncomeTypes()[0].id) {
        totalIncome +=
          helperService.isNumber(filteredIncome[i].payg.grossSalary, true) *
            helperService.getFrequencyValue(
              filteredIncome[i].payg.grossSalaryFrequency,
              false
            ) +
          helperService.isNumber(filteredIncome[i].payg.bonus, true) *
            helperService.getFrequencyValue(filteredIncome[i].payg.bonusFrequency, false) +
          helperService.isNumber(filteredIncome[i].payg.overtimeEssential, true) *
            helperService.getFrequencyValue(
              filteredIncome[i].payg.overtimeEssentialFrequency,
              false
            ) +
          helperService.isNumber(filteredIncome[i].payg.overtimeNonEssential, true) *
            helperService.getFrequencyValue(
              filteredIncome[i].payg.overtimeNonEssentialFrequency,
              false
            ) +
          helperService.isNumber(filteredIncome[i].payg.commission, true) *
            helperService.getFrequencyValue(filteredIncome[i].payg.commissionFrequency, false) +
          helperService.isNumber(filteredIncome[i].payg.allowance, true) *
            helperService.getFrequencyValue(filteredIncome[i].payg.allowanceFrequency, false);
      } else if (
        helperService.getOrganizationCountry().cca2 === 'AU' &&
        filteredIncome[i].incomeType.id === getAvailableIncomeTypes()[1].id
      ) {
        if (workflow?.isAssetFinance()) {
          totalIncome +=
            helperService.isNumber(
              filteredIncome[i]?.business?.lastTaxYear?.profitBeforeTax || 0,
              true
            ) +
            helperService.isNumber(
              filteredIncome[i]?.business?.lastTaxYear?.depreciation || 0,
              true
            ) +
            helperService.isNumber(
              filteredIncome[i]?.business?.lastTaxYear?.interestAndOtherAddbacks || 0,
              true
            ) +
            helperService.isNumber(
              filteredIncome[i]?.business?.lastTaxYear?.leaseRentals || 0,
              true
            ) +
            helperService.isNumber(
              filteredIncome[i]?.business?.lastTaxYear?.amortisation || 0,
              true
            ) +
            helperService.isNumber(
              filteredIncome[i]?.business?.lastTaxYear?.lossOnSaleOfFixedAsset || 0,
              true
            ) -
            helperService.isNumber(
              filteredIncome[i]?.business?.lastTaxYear?.profitOnSaleOfFixedAsset || 0,
              true
            ) +
            helperService.isNumber(
              filteredIncome[i]?.business?.lastTaxYear?.nonRecurringExpense || 0,
              true
            ) -
            helperService.isNumber(
              filteredIncome[i]?.business?.lastTaxYear?.taxPaid || 0,
              true
            );
        } else if (workflow?.isHomeLoan()) {
          totalIncome += 0;
        } else {
          totalIncome +=
            (helperService.isNumber(
              filteredIncome[i].business.lastTaxYear.profitBeforeTax,
              true
            ) +
              helperService.isNumber(
                filteredIncome[i].business.lastTaxYear.interestAndOtherAddbacks,
                true
              ) +
              helperService.isNumber(
                filteredIncome[i].business.lastTaxYear.depreciation,
                true
              ) +
              helperService.isNumber(
                filteredIncome[i].business.previousTaxYear.profitBeforeTax,
                true
              ) +
              helperService.isNumber(
                filteredIncome[i].business.previousTaxYear.interestAndOtherAddbacks,
                true
              ) +
              helperService.isNumber(
                filteredIncome[i].business.previousTaxYear.depreciation,
                true
              )) /
            2;
        }
      } else if (
        helperService.getOrganizationCountry().cca2 === 'NZ' &&
        filteredIncome[i].incomeType.id === getAvailableIncomeTypes()[1].id
      ) {
        hasTaxPaid =
          helperService.isNumber(filteredIncome[i].business.lastTaxYear.taxPaid, true) > 0;
        totalIncome +=
          helperService.isNumber(filteredIncome[i].business.lastTaxYear.profitBeforeTax, true) +
          helperService.isNumber(filteredIncome[i].business.lastTaxYear.depreciation, true) +
          helperService.isNumber(
            filteredIncome[i].business.lastTaxYear.interestAndOtherAddbacks,
            true
          ) -
          helperService.isNumber(filteredIncome[i].business.lastTaxYear.taxPaid, true);
      } else if (filteredIncome[i].incomeType.id === getAvailableIncomeTypes()[2].id) {
        totalIncome +=
          helperService.isNumber(filteredIncome[i].grossValue, true) *
          helperService.getFrequencyValue(filteredIncome[i].frequency, false);
      } else if (filteredIncome[i].incomeType.id === getAvailableIncomeTypes()[3].id) {
        totalNontaxable +=
          helperService.isNumber(filteredIncome[i].grossValue, true) *
          helperService.getFrequencyValue(filteredIncome[i].frequency, false);
      }
    }
    if (helperService.getOrganizationCountry().cca2 === 'AU' && workflow?.isHomeLoan()) {
      const totalBusiness = getTotalNetBusinessIncomePerYearForHomeLoans(filteredIncome, false);
      const totalDepreciation = getTotalNetBusinessIncomePerYearForHomeLoans(
        filteredIncome,
        true
      );
      totalIncome += totalBusiness;
      totalNontaxable += totalDepreciation;
    }
    if (!hasIncomePrimaryBuffer && mfpType === 'proposedPositionBuffer') {
      totalIncome *= totalIncomeShadingBuffer;
    }

    if (!hasTaxPaid && netIncome && netIncome.length) {
      for (let n = 0; n < netIncome.length; n += 1) {
        if (
          (totalIncome >= netIncome[n].incomeFrom && totalIncome <= netIncome[n].incomeTo) ||
          (totalIncome >= netIncome[n].incomeFrom && netIncome[n].incomeTo === '')
        ) {
          totalNetIncome =
            totalIncome -
            ((totalIncome - (netIncome[n].incomeFrom - 1)) * netIncome[n].tax +
              netIncome[n].additionalTax) -
            totalIncome * netIncome[n].medicareLevy +
            totalNontaxable;
        }
      }
    } else {
      totalNetIncome = totalIncome;
    }
    return totalNetIncome;
  };

  /**
   * Get total net income per contact.
   *
   * @param {Array} income
   * @param {String} mfpType
   * @param {Array} contact
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   * @param {Object} workflow
   * @param {Boolean} isDealOwnerBroker
   * @param {Boolean} haveLendersExisting
   *
   * @returns {Object}
   */
  const getTotalAllNetIncome = ({
    income,
    mfpType,
    contacts,
    assets,
    homeLoanSecurities,
    workflow,
    isDealOwnerBroker,
    haveLendersExisting
  }) => {
    let total = 0;
    for (let i = 0; i < contacts.length; i += 1) {
      total += getTotalNetIncomeByContact({
        income,
        mfpType,
        idContact: contacts[i].id,
        assets,
        homeLoanSecurities,
        workflow,
        isDealOwnerBroker,
        haveLendersExisting
      });
    }
    return { monthly: total / 12, annual: total };
  };

  /**
   * Get total income by contact, by income type.
   *
   * @param {Array} income
   * @param {String} incomeType
   * @param {String} businessYear
   * @param {Boolean} isBusinessDepreciation
   * @param {String} idContact
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   * @param {String} workflowType
   * @param {Boolean} isDealOwnerBroker
   * @param {Boolean} haveLendersExisting
   * @param {Boolean} isUsingForFinanceCalculations
   *
   * @returns {Number}
   */
  const getTotalIncomeByContactByIncomeType = ({
    income,
    incomeType,
    businessYear,
    isBusinessDepreciation,
    idContact,
    assets,
    homeLoanSecurities,
    workflowType,
    isDealOwnerBroker,
    haveLendersExisting,
    isUsingForFinanceCalculations
  }) => {
    let total = 0;
    let filteredIncome = [];
    if (income && income.length) {
      filteredIncome = income.filter(
        (i) => i.incomeType.id === incomeType && i.idContact === idContact
      );
    }
    if (incomeType === 'rental') {
      const currentRentalIncome =
        getRentalOrBoarderIncomeOfExistingPropertiesByContact({
          assets,
          idContact,
          ownershipType: OwnershipType.investment,
          isUsingForFinanceCalculations
        }) * 12;
      const proposedRentalIncome =
        getRentalOrBoarderIncomeOfHomeLoanSecuritiesByContact({
          homeLoanSecurities,
          assets,
          idContact,
          ownershipType: OwnershipType.investment,
          workflowType,
          isDealOwnerBroker,
          haveLendersExisting,
          isUsingForFinanceCalculations
        }) * 12;
      total += currentRentalIncome + proposedRentalIncome;
    } else if (
      incomeType === 'boarder' &&
      helperService.getOrganizationCountry().cca2 === 'NZ'
    ) {
      const currentBoarderIncome =
        getRentalOrBoarderIncomeOfExistingPropertiesByContact({
          assets,
          idContact,
          ownershipType: OwnershipType.owner_occupied,
          isUsingForFinanceCalculations
        }) * 12;
      const proposedBoarderIncome =
        getRentalOrBoarderIncomeOfHomeLoanSecuritiesByContact({
          homeLoanSecurities,
          assets,
          idContact,
          ownershipType: OwnershipType.owner_occupied,
          workflowType,
          isDealOwnerBroker,
          haveLendersExisting,
          isUsingForFinanceCalculations
        }) * 12;
      total += currentBoarderIncome + proposedBoarderIncome;
    } else {
      for (let i = 0; i < filteredIncome.length; i += 1) {
        if (incomeType === getAvailableIncomeTypes()[0].id) {
          total +=
            helperService.isNumber(filteredIncome[i].payg.grossSalary, true) *
              helperService.getFrequencyValue(
                filteredIncome[i].payg.grossSalaryFrequency,
                false
              ) +
            helperService.isNumber(filteredIncome[i].payg.bonus, true) *
              helperService.getFrequencyValue(filteredIncome[i].payg.bonusFrequency, false) +
            helperService.isNumber(filteredIncome[i].payg.overtimeEssential, true) *
              helperService.getFrequencyValue(
                filteredIncome[i].payg.overtimeEssentialFrequency,
                false
              ) +
            helperService.isNumber(filteredIncome[i].payg.overtimeNonEssential, true) *
              helperService.getFrequencyValue(
                filteredIncome[i].payg.overtimeNonEssentialFrequency,
                false
              ) +
            helperService.isNumber(filteredIncome[i].payg.commission, true) *
              helperService.getFrequencyValue(
                filteredIncome[i].payg.commissionFrequency,
                false
              ) +
            helperService.isNumber(filteredIncome[i].payg.allowance, true) *
              helperService.getFrequencyValue(filteredIncome[i].payg.allowanceFrequency, false);
        } else if (incomeType === getAvailableIncomeTypes()[1].id) {
          if (businessYear && typeof businessYear === 'string') {
            total +=
              helperService.isNumber(
                filteredIncome[i].business[businessYear].profitBeforeTax,
                true
              ) +
              helperService.isNumber(
                filteredIncome[i].business[businessYear].depreciation,
                true
              ) +
              helperService.isNumber(
                filteredIncome[i].business[businessYear].interestAndOtherAddbacks,
                true
              ) +
              helperService.isNumber(
                filteredIncome[i].business[businessYear].leaseRentals,
                true
              ) +
              helperService.isNumber(
                filteredIncome[i].business[businessYear].amortisation,
                true
              ) +
              helperService.isNumber(
                filteredIncome[i].business[businessYear].lossOnSaleOfFixedAsset,
                true
              ) -
              helperService.isNumber(
                filteredIncome[i].business[businessYear].profitOnSaleOfFixedAsset,
                true
              ) +
              helperService.isNumber(
                filteredIncome[i].business[businessYear].nonRecurringExpense,
                true
              ) -
              helperService.isNumber(filteredIncome[i].business[businessYear].taxPaid, true);
          } else if (
            helperService.getOrganizationCountry().cca2 === 'NZ' &&
            (workflowType === WorkflowType.available()[4].name ||
              workflowType === WorkflowType.available()[4])
          ) {
            total += isBusinessDepreciation
              ? helperService.isNumber(
                  filteredIncome[i].business.lastTaxYear.depreciation,
                  true
                )
              : helperService.isNumber(
                  filteredIncome[i].business.lastTaxYear.profitBeforeTax,
                  true
                ) +
                helperService.isNumber(
                  filteredIncome[i].business.lastTaxYear.depreciation,
                  true
                ) +
                helperService.isNumber(
                  filteredIncome[i].business.lastTaxYear.interestAndOtherAddbacks,
                  true
                ) -
                helperService.isNumber(filteredIncome[i].business.lastTaxYear.taxPaid, true);
          } else if (helperService.getOrganizationCountry().cca2 === 'AU') {
            total += isBusinessDepreciation
              ? (helperService.isNumber(
                  filteredIncome[i].business.lastTaxYear.depreciation,
                  true
                ) +
                  helperService.isNumber(
                    filteredIncome[i].business.previousTaxYear.depreciation,
                    true
                  )) /
                2
              : (helperService.isNumber(
                  filteredIncome[i].business.lastTaxYear.profitBeforeTax,
                  true
                ) +
                  helperService.isNumber(
                    filteredIncome[i].business.lastTaxYear.interestAndOtherAddbacks,
                    true
                  ) +
                  helperService.isNumber(
                    filteredIncome[i].business.previousTaxYear.profitBeforeTax,
                    true
                  ) +
                  helperService.isNumber(
                    filteredIncome[i].business.previousTaxYear.interestAndOtherAddbacks,
                    true
                  )) /
                2;
          }
        } else if (incomeType === getAvailableIncomeTypes()[2].id) {
          total +=
            helperService.isNumber(filteredIncome[i].grossValue, true) *
            helperService.getFrequencyValue(filteredIncome[i].frequency, false);
        } else if (incomeType === getAvailableIncomeTypes()[3].id) {
          total +=
            helperService.isNumber(filteredIncome[i].grossValue, true) *
            helperService.getFrequencyValue(filteredIncome[i].frequency, false);
        }
      }
    }
    if (
      helperService.getOrganizationCountry().cca2 === 'AU' &&
      (workflowType === WorkflowType.available()[4].name ||
        workflowType === WorkflowType.available()[4]) &&
      filteredIncome.length &&
      incomeType === getAvailableIncomeTypes()[1].id
    ) {
      total = isBusinessDepreciation
        ? getTotalNetBusinessIncomePerYearForHomeLoans(filteredIncome, true)
        : getTotalNetBusinessIncomePerYearForHomeLoans(filteredIncome, false);
    }
    return total;
  };

  /**
   * Get monthly rental income of new investments per contact.
   *
   * @param {Array} income
   * @param {String} idContact
   * @param {Array} contacts
   * @param {Array} assets
   * @param {Array} homeLoanSecurities
   * @param {String} workflowType
   * @param {Boolean} isDealOwnerBroker
   * @param {Boolean} haveLendersExisting
   * @param {Boolean} isUsingForFinanceCalculations
   *
   * @returns {Number}
   */
  const getTotalAllIncomeByContact = ({
    income,
    idContact,
    contacts,
    assets,
    homeLoanSecurities,
    workflowType,
    isDealOwnerBroker,
    haveLendersExisting,
    isUsingForFinanceCalculations
  }) => {
    let total = 0;
    const contact = _.find(contacts, (c) => c.id === idContact);
    if (contact) {
      total += getTotalIncomeByContactByIncomeType({
        income,
        incomeType: 'rental',
        idContact,
        assets,
        homeLoanSecurities,
        workflowType,
        isDealOwnerBroker,
        haveLendersExisting,
        isUsingForFinanceCalculations
      });
      if (helperService.getOrganizationCountry().cca2 === 'NZ') {
        total += getTotalIncomeByContactByIncomeType({
          income,
          incomeType: 'boarder',
          idContact,
          assets,
          homeLoanSecurities,
          workflowType,
          isDealOwnerBroker,
          haveLendersExisting,
          isUsingForFinanceCalculations
        });
      }
      const availableIIncomeTypes = getAvailableIncomeTypes();
      for (let i = 0; i < availableIIncomeTypes.length; i += 1) {
        if (i === 0 || (i === 2 && !contact.isCompany) || i === 3) {
          total += getTotalIncomeByContactByIncomeType({
            income,
            incomeType: availableIIncomeTypes[i].id,
            idContact,
            assets,
            homeLoanSecurities,
            workflowType,
            isDealOwnerBroker,
            haveLendersExisting,
            isUsingForFinanceCalculations
          });
        } else if (i === 1) {
          total += getTotalIncomeByContactByIncomeType({
            income,
            incomeType: availableIIncomeTypes[i].id,
            businessYear:
              workflowType === WorkflowType.available()[1].name ||
              workflowType === WorkflowType.available()[1]
                ? 'lastTaxYear'
                : null,
            idContact,
            assets,
            homeLoanSecurities,
            workflowType,
            isDealOwnerBroker,
            haveLendersExisting,
            isUsingForFinanceCalculations
          });
        }
      }
    }
    return total;
  };

  return {
    getAvailableIncomeTypes,
    incomeAdd,
    incomeDelete,
    getBusinessYear,
    getTotalPaygIncomePerContact,
    getTotalBusinessIncomePerContact,
    getTotalIncomeByContactByIncomeType,
    getTotalAllIncomeByContact,
    getTotalNetIncomeByContact,
    getTotalAllNetIncome
  };
};
